import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useImmerReducer } from "use-immer";
import io from "socket.io-client";

import RegisterInvite from "./components/RegisterInvite.js";
import Notification from "./components/Notification.js";
import About from "./components/About.js";
import Terms from "./components/Terms.js";
import Privacy from "./components/Privacy.js";
import Dispute from "./components/Dispute.js";
import Support from "./components/Support.js";
import InvitationsAdmin from "./components/InvitationsAdmin.js";
import V3Home from "./components/V3Home.js";
import V3Login from "./components/V3Login.js";
import V3Register from "./components/V3Register.js";
import V3Account from "./components/V3Account.js";
import V3AuthGuard from "./components/V3AuthGuard.js";
import KeycloakAuth from "./components/KeycloakAuth.js";

// V4 Components
import V4Home from "./components/v4/Home.js";
import V4Login from "./components/v4/Login.js";
import V4Register from "./components/v4/Register.js";
import V4Account from "./components/v4/Account.js";
import V4AuthGuard from "./components/v4/AuthGuard.js";
import V4Layout from "./components/v4/Layout.js";
import Demo from "./components/Demo.js";
import DemoOmnia from "./components/DemoOmnia.js";
import LiveStreamView from "./components/LiveStreamView.js";
import SiteView from "./components/SiteView.js";
import PersonView from "./components/PersonView.js";
import RadioView from "./components/RadioView.js";
import ProgramView from "./components/ProgramView.js";
import EpisodeView from "./components/EpisodeView.js";
import NewsArticleView from "./components/NewsArticleView.js";
import PlataPage from "./components/PlataPage.js";
import PartnerFeed from "./components/v4/PartnerFeed.js";

import StateContext from "./StateContext.js";
import DispatchContext from "./DispatchContext.js";

import "./style.css";

function App() {
  const initialState = {
    orientation: "landscape",
    activeEvent: { header: "", message: "", link: "" },
    muted: true,
    isPlayerPlaying: false,
    isTrayVisible: true,
    trayContent: null,
    isTimerStopped: false,
    isLoggedIn: false,
    userData: null,
    isInitialLoad: true,
    showSplash: false, // nu mai folosim overlay-ul separat
    showMenuFade: false,
    isBlocked: false,
    videoVisible: false,
  };

  function appReducer(draft, action) {
    switch (action.type) {
      case "setOrientation": {
        draft.orientation = action.value;
        return;
      }
      case "newEvent": {
        draft.activeEvent = action.value;
        return;
      }
      case "toggleMute": {
        draft.muted = !draft.muted;
        return;
      }
      case "setTrayVisible": {
        draft.isTrayVisible = action.value;
        if (action.value) {
          draft.isInitialLoad = false;
        }
        return;
      }
      case "playerPlaying": {
        draft.isPlayerPlaying = true;
        return;
      }
      case "setTrayContent": {
        draft.trayContent = action.value;
        return;
      }
      case "stopTimer": {
        draft.isTimerStopped = true;
        return;
      }
      case "resetTimer": {
        draft.isTimerStopped = false;
        return;
      }
      case "userLoggedIn": {
        draft.isLoggedIn = true;
        draft.userData = action.payload;
        return;
      }
      case "userLoggedOut": {
        draft.isLoggedIn = false;
        draft.userData = null;
        return;
      }
      case "setSplash": {
        draft.showSplash = action.value;
        return;
      }
      case "setMenuFade": {
        draft.showMenuFade = action.value;
        return;
      }
      case "setBlocked": {
        draft.isBlocked = action.value;
        return;
      }
      case "setVideoVisible": {
        draft.videoVisible = action.value;
        return;
      }
      default:
        return draft;
    }
  }

  const [state, dispatch] = useImmerReducer(appReducer, initialState);

  // Verificăm dacă există un token salvat în localStorage la încărcarea aplicației
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('userData');

    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        dispatch({ type: "userLoggedIn", payload: userData });
      } catch (error) {
        console.error('Eroare la parsarea datelor utilizatorului:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
    
    // Redirect la v3.0 dacă suntem pe ruta root
    if (window.location.pathname === '/') {
      window.location.href = '/v3.0';
    }
  }, []);



  // Stop timer on menu click
  const handleMenuClick = () => {
    dispatch({ type: "stopTimer" });
  };

  // Catch events from WS - socket.io connection only when user is logged in
  useEffect(() => {
    let socket;
    // Inițializăm socket-ul doar dacă utilizatorul este autentificat
    if (state.isLoggedIn) {
      socket = io(process.env.WS_ADDRESS);

      socket.on("newsyncurl", (data) => {
        try {
          let header = "";
          let message = "";
          let link = "";

          if (data.event === "App\\Events\\NunzioStoriesEvent") {
            header = "Citește pe alephnews.ro:";
            message = data.data.message.title;
            if (data.data.message.an_id != null) {
              link = "https://alephnews.ro/?p=" + data.data.message.an_id;
            } else {
              link = data.data.message.forced_link;
            }
          }

          if (data.event === "App\\Events\\NunzioAdsEvent") {
            header = "RECLAMĂ";
            message = "Vizitează clientul nostru";
            if (data.data.message.client != null) {
              message += data.data.message.client;
            }
            link = data.data.message.ad_url;
          }

          let payload = {
            header: header,
            message: message,
            link: link,
          };

          if (!state.isTrayVisible) {
            dispatch({
              type: "setTrayContent",
              value: () => <Notification payload={payload} />,
            });
            dispatch({ type: "setTrayVisible", value: true });

            handleMenuClick();

            setTimeout(() => {
              dispatch({ type: "setTrayVisible", value: false });
              dispatch({ type: "setTrayContent", value: null });
            }, 5000);
          }
        } catch (err) {
          console.error("Eroare în handlerul newsyncurl:", err, data);
        }
      });

      socket.on("error", (err) => {
        console.error("[WS] Socket error:", err);
      });
      socket.on("connect_error", (err) => {
        console.error("[WS] Socket connect_error:", err);
      });
    }
    return () => {
      if (socket) {
        socket.off("newsyncurl");
        socket.off("error");
        socket.off("connect_error");
        socket.disconnect();
      }
    };
  }, [state.isLoggedIn, dispatch]);


  useEffect(() => {
    if (state.isLoggedIn && state.isInitialLoad) {
      const timer = setTimeout(() => {
        dispatch({ type: "setTrayVisible", value: false });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [state.isLoggedIn, state.isInitialLoad]);


  // Adaug un useEffect separat pentru delay de 20s la verificarea autentificării
  useEffect(() => {
    const authTimer = setTimeout(() => {
      checkAuthAndSubscription();
    }, 20000); // 20 secunde
    return () => clearTimeout(authTimer);
  }, []);

  async function checkAuthAndSubscription() {
    // Verifică dacă suntem în mod demo - dacă da, setăm utilizatorul ca logat și oprim verificările
    const demoMode = localStorage.getItem('demo_mode');
    if (demoMode === 'omnia') {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');
      if (user && user.email) {
        dispatch({ type: "userLoggedIn", payload: user });
        dispatch({ type: "setBlocked", value: false });
      }
      return; // Nu facem verificări suplimentare în mod demo
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      dispatch({ type: "userLoggedOut" });
      // Redirect la v3.0 login dacă nu e autentificat
      if (window.location.pathname === '/') {
        window.location.href = '/v3.0/login';
      }
      return;
    }
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('userData') || '{}');
    } catch {
      user = null;
    }
    if (!user || !user.email) {
      dispatch({ type: "userLoggedOut" });
      // Redirect la v3.0 login dacă nu e autentificat
      if (window.location.pathname === '/') {
        window.location.href = '/v3.0/login';
      }
    } else {
      // Dacă subscripția este de tip Apple, verificăm periodic statusul; altfel presupunem activă
      if (user.subscription_type && user.subscription_type.toLowerCase() === 'apple') {
        try {
          const res = await fetch("https://backend.alephmedia.ro/api/account/check-apple-subscription", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ email: user.email })
          });
          const data = await res.json();
          if (!data.success || data.data.apple_subscription_status !== 'active') {
            dispatch({ type: "userLoggedOut" });
            // Redirect la v3.0 login dacă subscripția nu e activă
            if (window.location.pathname === '/') {
              window.location.href = '/v3.0/login';
            }
          } else {
            dispatch({ type: "userLoggedIn", payload: user });
            dispatch({ type: "setBlocked", value: false });
          }
        } catch {
          // Dacă nu putem verifica (network error), nu deconectăm utilizatorul; păstrăm sesiunea activă
          console.warn("Eroare la verificarea subscripției Apple – păstrez utilizatorul logat");
          dispatch({ type: "userLoggedIn", payload: user });
        }
      } else {
        // Alte tipuri de subscripție: considerăm user-ul valid
        dispatch({ type: "userLoggedIn", payload: user });
        dispatch({ type: "setBlocked", value: false });
      }
    }
  }


  // Blocare interacțiuni dacă isBlocked
  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {state.showSplash ? (
          <div className="splash-screen">
            <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" />
          </div>
        ) : (
          <BrowserRouter>
            <Routes>
              <Route path="/register/invite/:token" element={<RegisterInvite />} />
              <Route path="/admin/invitations" element={<InvitationsAdmin />} />
              
              <Route path="/demo" element={<Demo />} />
              <Route path="/demo-omnia" element={<DemoOmnia />} />
              <Route path="/plata" element={<PlataPage />} />
              <Route path="/v3.0" element={<V3AuthGuard><V3Home /></V3AuthGuard>} />
              <Route path="/v3.0/login" element={<V3Login />} />
              <Route path="/auth/keycloak" element={<KeycloakAuth />} />
              <Route path="/v3.0/register" element={<V3Register />} />
              <Route path="/v3.0/account" element={<V3AuthGuard><V3Account /></V3AuthGuard>} />
              <Route path="/v3.0/live/:id" element={<V3AuthGuard><LiveStreamView /></V3AuthGuard>} />
              <Route path="/v3.0/sites/:id" element={<V3AuthGuard><SiteView /></V3AuthGuard>} />
              <Route path="/v3.0/people/:id" element={<V3AuthGuard><PersonView /></V3AuthGuard>} />
              <Route path="/v3.0/radios/:id" element={<V3AuthGuard><RadioView /></V3AuthGuard>} />
              <Route path="/v3.0/programs/:id" element={<V3AuthGuard><ProgramView /></V3AuthGuard>} />
              <Route path="/v3.0/programs/:id/episodes/:episodeId" element={<V3AuthGuard><EpisodeView /></V3AuthGuard>} />
              <Route path="/v3.0/news/:slug" element={<V3AuthGuard><NewsArticleView /></V3AuthGuard>} />
              
              {/* V4 Routes */}
              <Route path="/v4.0" element={<V4AuthGuard><V4Home /></V4AuthGuard>} />
              <Route path="/v4.0/login" element={<V4Login />} />
              <Route path="/v4.0/register" element={<V4Register />} />
              <Route path="/v4.0/account" element={<V4AuthGuard><V4Layout><V4Account /></V4Layout></V4AuthGuard>} />
              <Route path="/v4.0/partner-feed" element={<V4AuthGuard><V4Layout><PartnerFeed /></V4Layout></V4AuthGuard>} />
              <Route path="/v4.0/live/:id" element={<V4AuthGuard><LiveStreamView /></V4AuthGuard>} />
              <Route path="/v4.0/sites/:id" element={<V4AuthGuard><V4Layout><SiteView /></V4Layout></V4AuthGuard>} />
              <Route path="/v4.0/people/:id" element={<V4AuthGuard><V4Layout><PersonView /></V4Layout></V4AuthGuard>} />
              <Route path="/v4.0/radios/:id" element={<V4AuthGuard><V4Layout><RadioView /></V4Layout></V4AuthGuard>} />
              <Route path="/v4.0/programs/:id" element={<V4AuthGuard><V4Layout><ProgramView /></V4Layout></V4AuthGuard>} />
              <Route path="/v4.0/programs/:id/episodes/:episodeId" element={<V4AuthGuard><EpisodeView /></V4AuthGuard>} />
              <Route path="/v4.0/news/:slug" element={<V4AuthGuard><V4Layout><NewsArticleView /></V4Layout></V4AuthGuard>} />
              
              <Route path="/support" element={<Support />} />
              <Route path="/about" element={<About />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/dispute" element={<Dispute />} />
              
              {/* Ruta implicită redirectează la v3.0 */}
              <Route path="/" element={<V3AuthGuard><V3Home /></V3AuthGuard>} />
              <Route path="*" element={<V3AuthGuard><V3Home /></V3AuthGuard>} />
            </Routes>
          </BrowserRouter>
        )}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

const container = document.getElementById("app");

if (!container._reactRootContainer) {
  const root = ReactDOM.createRoot(container);
  root.render(<App />);
  container._reactRootContainer = root;
} else {
  container._reactRootContainer.render(<App />);
}

if (module.hot) {
  module.hot.accept();
}

export default App;
